
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Coins, Users, Home, LogOut, Menu, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/AuthContext";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useSubscription } from "@/lib/SubscriptionContext";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

interface TopNavigationProps {
  toggleSidebar: () => void;
}

const TopNavigation = ({ toggleSidebar }: TopNavigationProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut, user } = useAuth();
  const { userPlan, isLoading: subscriptionLoading } = useSubscription();

  const menuItems = [
    {
      name: "Home",
      path: "/home",
      icon: <Home className="h-5 w-5" />,
    },
    {
      name: "Chit Pools",
      path: "/chit-pools",
      icon: <Users className="h-5 w-5" />,
    },
    {
      name: "Loans",
      path: "/loans",
      icon: <Coins className="h-5 w-5" />,
    }
  ];

  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      toast("Signed out successfully");
      navigate("/"); // Navigate to index page after sign out
    } catch (error) {
      console.error("Error signing out:", error);
      toast.error("Failed to sign out");
    }
  };

  const handleNavigateToCustomers = () => {
    navigate("/customers");
  };

  const handleNavigateToPricing = () => {
    navigate("/#pricing");
  };

  // Get user name from metadata
  const userName = user?.user_metadata?.name || user?.email?.split('@')[0] || 'User';
  // Get user initials for avatar
  const userInitials = userName.split(' ').map(n => n[0]).join('').toUpperCase();

  // Generate badge variant based on plan name
  const getBadgeVariant = () => {
    if (!userPlan) return "default";
    
    switch (userPlan.name) {
      case "Premium":
        return "default";
      case "Standard":
        return "secondary";
      default:
        return "outline";
    }
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-20 bg-background border-b h-16 flex items-center px-4 justify-between">
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon" 
          className="md:hidden" 
          onClick={toggleSidebar}
        >
          <Menu className="h-5 w-5" />
          <span className="sr-only">Toggle menu</span>
        </Button>
        
        <div 
          className="flex items-center gap-2 cursor-pointer" 
          onClick={() => navigate("/")}
        >
          <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
            <span className="text-primary-foreground font-bold">C</span>
          </div>
          <h1 className="text-lg font-semibold">Chito</h1>
        </div>
        
        <nav className="hidden md:flex items-center gap-1">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                "flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                isActive(item.path)
                  ? "bg-primary text-primary-foreground"
                  : "hover:bg-muted"
              )}
            >
              {item.icon}
              {item.name}
            </Link>
          ))}
        </nav>
      </div>
      
      <div className="flex items-center gap-2">
        {!subscriptionLoading && userPlan && (
          <Badge 
            variant={getBadgeVariant()} 
            className="cursor-pointer hover:opacity-80 transition-opacity"
            onClick={handleNavigateToPricing}
          >
            {userPlan.name || "Free"} Plan
          </Badge>
        )}
        <span className="text-sm font-medium mr-1 hidden sm:inline-block">{userName}</span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="rounded-full" size="icon">
              <Avatar className="h-8 w-8 bg-primary text-primary-foreground">
                <AvatarFallback>{userInitials}</AvatarFallback>
              </Avatar>
              <span className="sr-only">User menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handleNavigateToCustomers}>
              My Customers
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleNavigateToPricing}>
              Subscription Plans
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleSignOut}>
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default TopNavigation;
